import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { getClientProfile, getClientStatement, uploadsUrl } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useOrgSettings, useFirmName } from '../contexts/OrgSettingsContext';
import { generateSoaPdf } from '../utils/soaPdf';
import { showToast } from '../utils/toast';

// ── Types ─────────────────────────────────────────────────────────────────────

interface KPIs {
  openTasks: number; overdueTasks: number; totalTasks: number;
  ytdBilling: number; lifetimeBilling: number;
  totalOutstanding: number; totalPaid: number; totalTdsAmount: number;
  totalHours: number; totalInvoices: number;
}

interface GstinEntry {
  id: number; label?: string; gstin?: string; gstType: string;
  address?: string; city?: string; state?: string; pincode?: string; isPrimary: boolean;
}

interface VaultDoc {
  id: number; title: string; fileName: string; category?: string;
  originalName?: string; fileSize?: number; mimeType?: string;
  createdAt: string; uploadedBy?: { id: number; staffName: string };
  notes?: string;
}

interface TdsCertificate {
  id: number; certificateNo: string; section: string; fiscalYear: string;
  period?: string; amountAsPer26AS: number; receivedDate?: string; notes?: string;
}

interface Lead {
  id: number; leadName: string; stage?: string; createdAt: string;
}

interface ConvertedLead {
  id: number; leadName: string; createdAt: string;
}

interface ClientData {
  id: number; clientCode: string; clientName: string; legalName?: string;
  pan?: string; phone?: string; gstin?: string; address?: string;
  referringCaName?: string; referringCaPhone?: string;
  gstins: GstinEntry[];
  vaultDocuments: VaultDoc[];
  tdsCertificates: TdsCertificate[];
  convertedFromLead?: ConvertedLead;
  referredLeads: Lead[];
  createdAt: string;
}

interface Staff { id: number; staffName: string }
interface SubTask { id: number; subTaskNumber: string; name: string; status: string; assignedTo?: Staff }
interface TimesheetEntry { hoursSpent: number }

interface Task {
  id: number; taskId: string; taskName: string; status: string;
  billingStatus: string; dueDate?: string; udin?: string;
  billedAmount?: number;
  partner?: Staff; manager?: Staff;
  category?: { id: number; name: string };
  profitCentre?: { id: number; name: string };
  billingEntity?: { id: number; name: string };
  assignedStaff: { staff: Staff }[];
  subtasks: SubTask[];
  timesheets: TimesheetEntry[];
  createdAt: string;
}

interface Payment {
  id: number; paymentDate: string; amount: number; mode: string; reference?: string; notes?: string;
}

interface Invoice {
  id: number; invoiceNumber: string; invoiceDate: string; dueDate?: string;
  status: string; totalAmount?: number; amount?: number;
  tdsSection?: string; tdsRate?: number; tdsAmount?: number;
  taxType?: string; notes?: string;
  task?: { id: number; taskId: string; taskName: string };
  billingEntity?: { id: number; name: string };
  payments: Payment[];
  // Set when the invoice was raised on a party other than the client the work
  // was done for: clientId is the billed party, serviceClient the real client.
  clientId?: number;
  client?: { id: number; clientName: string };
  serviceClient?: { id: number; clientName: string } | null;
}

interface RecentActivity {
  id: number; date: string; hoursSpent: number;
  staff: Staff;
  task: { id: number; taskId: string; taskName: string };
}

interface ProfileData {
  client: ClientData;
  kpis: KPIs;
  tasks: Task[];
  invoices: Invoice[];
  recentActivity: RecentActivity[];
  people: { id: number; staffName: string; role: string }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const fmtDate = (d?: string | null) =>
  d ? format(new Date(d), 'dd-MMM-yy') : '—';

const statusColor = (s: string) => {
  const m: Record<string, string> = {
    OPEN: 'bg-blue-100 text-blue-700',
    IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
    COMPLETED: 'bg-green-100 text-green-700',
    CANCELLED: 'bg-gray-100 text-gray-500',
    ON_HOLD: 'bg-orange-100 text-orange-700',
  };
  return m[s] || 'bg-gray-100 text-gray-600';
};

const invStatusColor = (s: string) => {
  const m: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-600',
    SENT: 'bg-blue-100 text-blue-700',
    PARTIAL: 'bg-yellow-100 text-yellow-700',
    PAID: 'bg-green-100 text-green-700',
    OVERDUE: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-gray-200 text-gray-500',
  };
  return m[s] || 'bg-gray-100 text-gray-600';
};

const outstanding = (inv: Invoice) => {
  const total = Number(inv.totalAmount ?? inv.amount ?? 0);
  const tds = Number(inv.tdsAmount ?? 0);
  const paid = inv.payments.reduce((s, p) => s + Number(p.amount), 0);
  return Math.max(0, total - tds - paid);
};

// ── Sub-components ────────────────────────────────────────────────────────────

const KPICard: React.FC<{ label: string; value: string; sub?: string; color?: string }> =
  ({ label, value, sub, color = 'text-gray-900' }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );

// ── Tabs ──────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'engagements' | 'financials' | 'compliance' | 'documents' | 'people' | 'referrals';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview',     label: 'Overview' },
  { id: 'engagements',  label: 'Engagements' },
  { id: 'financials',   label: 'Financials' },
  { id: 'compliance',   label: 'Compliance' },
  { id: 'documents',    label: 'Documents' },
  { id: 'people',       label: 'People' },
  { id: 'referrals',    label: 'Referrals' },
];

// ── Overview Tab ──────────────────────────────────────────────────────────────

const OverviewTab: React.FC<{ data: ProfileData }> = ({ data }) => {
  const { tasks, recentActivity } = data;
  const upcoming = tasks
    .filter(t => !['COMPLETED', 'CANCELLED'].includes(t.status) && t.dueDate)
    .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
    .slice(0, 6);

  return (
    <div className="space-y-3">
      {/* Task Status Summary — inline horizontal strip */}
      <div className="flex flex-wrap gap-2">
        {['OPEN','IN_PROGRESS','ON_HOLD','COMPLETED','CANCELLED'].map(s => {
          const cnt = data.tasks.filter(t => t.status === s).length;
          return (
            <div key={s} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${cnt > 0 ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColor(s)}`}>{s.replace('_', ' ')}</span>
              <span className={`text-sm font-bold ${cnt > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{cnt}</span>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Upcoming Deadlines */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-700 text-xs uppercase tracking-wide">Upcoming Deadlines</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {upcoming.length === 0 ? (
              <p className="text-gray-400 text-xs p-4 text-center">No upcoming deadlines</p>
            ) : upcoming.map(t => {
              const due = new Date(t.dueDate!);
              const today = new Date(); today.setHours(0,0,0,0);
              const overdue = due < today;
              return (
                <div key={t.id} className="px-3 py-2 flex items-center justify-between gap-3 hover:bg-gray-50">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{t.taskName}</p>
                    <p className="text-[10px] text-gray-400">{t.taskId}</p>
                  </div>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${overdue ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                    {fmtDate(t.dueDate)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-700 text-xs uppercase tracking-wide">Recent Activity</h3>
          </div>
          <div className="divide-y divide-gray-50 max-h-[280px] overflow-y-auto">
            {recentActivity.length === 0 ? (
              <p className="text-gray-400 text-xs p-4 text-center">No recent activity</p>
            ) : recentActivity.slice(0, 8).map(a => (
              <div key={a.id} className="px-3 py-2 flex items-center justify-between gap-3 hover:bg-gray-50">
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{a.task.taskName}</p>
                  <p className="text-[10px] text-gray-500">{a.staff.staffName} · {fmtDate(a.date)}</p>
                </div>
                <span className="text-xs font-semibold text-indigo-600 whitespace-nowrap">
                  {Number(a.hoursSpent)}h
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Engagements Tab ───────────────────────────────────────────────────────────

const EngagementsTab: React.FC<{ tasks: Task[] }> = ({ tasks }) => {
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  const filtered = tasks.filter(t => {
    if (statusFilter !== 'ALL' && t.status !== statusFilter) return false;
    if (search && !t.taskName.toLowerCase().includes(search.toLowerCase()) &&
        !t.taskId.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="Search tasks..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          {['ALL','OPEN','IN_PROGRESS','ON_HOLD','COMPLETED','CANCELLED'].map(s => (
            <option key={s} value={s}>{s === 'ALL' ? 'All Statuses' : s.replace('_', ' ')}</option>
          ))}
        </select>
        <span className="text-xs text-gray-500">{filtered.length} task{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <th className="text-left px-4 py-3">Task</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Partner</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Hours</th>
              <th className="text-right px-4 py-3">Sub-tasks</th>
              <th className="text-left px-4 py-3">Due</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-gray-400 py-8">No tasks found</td></tr>
            ) : filtered.map(t => {
              const hours = (t.timesheets ?? []).reduce((s, ts) => s + Number(ts.hoursSpent), 0);
              const today = new Date(); today.setHours(0,0,0,0);
              const overdue = !['COMPLETED','CANCELLED'].includes(t.status) && t.dueDate && new Date(t.dueDate) < today;
              return (
                <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{t.taskName}</p>
                    <p className="text-xs text-gray-400">{t.taskId}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(t.status)}`}>
                      {t.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{t.partner?.staffName || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{t.category?.name || '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-700 font-medium">{hours > 0 ? `${hours}h` : '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-600">
                    {t.subtasks.length > 0 ? (
                      <span>{t.subtasks.filter(s => s.status === 'COMPLETED').length}/{t.subtasks.length}</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {t.dueDate ? (
                      <span className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {fmtDate(t.dueDate)}{overdue ? ' ⚠' : ''}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Financials Tab ────────────────────────────────────────────────────────────

const FinancialsTab: React.FC<{ invoices: Invoice[]; kpis: KPIs; client: ClientData }> = ({ invoices, kpis, client }) => {
  const [fyFilter, setFyFilter] = useState('ALL');
  const { settings } = useOrgSettings();
  const firmNameFromCtx = useFirmName();
  const [showSoa, setShowSoa] = useState(false);
  const [soaFrom, setSoaFrom] = useState('');
  const [soaTo, setSoaTo] = useState('');
  const [soaBusy, setSoaBusy] = useState(false);

  const downloadSoa = async () => {
    setSoaBusy(true);
    try {
      const res = await getClientStatement(client.id, {
        from: soaFrom || undefined,
        to: soaTo || undefined,
      });
      const firmName = firmNameFromCtx;
      const doc = generateSoaPdf(res.data, { name: firmName });
      const fileName = `SOA_${client.clientCode}_${new Date().toISOString().slice(0,10)}.pdf`;
      doc.save(fileName);
      setShowSoa(false);
      showToast('Statement of Account downloaded', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to generate SOA', 'error');
    } finally {
      setSoaBusy(false);
    }
  };

  // Derive unique FYs from invoice dates
  const fiscalYear = (d: string) => {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = dt.getMonth();
    return m >= 3 ? `${y}-${y+1}` : `${y-1}-${y}`;
  };
  const fys = ['ALL', ...Array.from(new Set(invoices.map(i => fiscalYear(i.invoiceDate)))).sort().reverse()];

  const filtered = fyFilter === 'ALL' ? invoices : invoices.filter(i => fiscalYear(i.invoiceDate) === fyFilter);
  // Raised on another party for this client's work: it is this client's
  // billing, but that party owes the money, so TDS/paid/outstanding skip it.
  const owedElsewhere = (i: Invoice) => i.clientId != null && i.clientId !== client.id;
  const owedHere = filtered.filter(i => !owedElsewhere(i));

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Total Outstanding" value={fmt(kpis.totalOutstanding)} color={kpis.totalOutstanding > 0 ? 'text-red-600' : 'text-green-600'} />
        <KPICard label="Total Billed" value={fmt(kpis.lifetimeBilling)} sub={`${kpis.totalInvoices} invoices`} />
        <KPICard label="Total Received" value={fmt(kpis.totalPaid)} />
        <KPICard label="TDS Deducted" value={fmt(kpis.totalTdsAmount)} />
      </div>

      {/* FY filter + SOA */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-gray-600 font-medium">Financial Year:</label>
        <select
          value={fyFilter}
          onChange={e => setFyFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          {fys.map(fy => <option key={fy} value={fy}>{fy === 'ALL' ? 'All Years' : `FY ${fy}`}</option>)}
        </select>
        <button
          onClick={() => setShowSoa(true)}
          className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          title="Generate Statement of Account PDF (invoices + opening balances + payments + TDS + credit notes)"
        >
          📄 Statement of Account
        </button>
      </div>

      {showSoa && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-xl">
            <h3 className="font-semibold text-gray-800 text-base mb-1">Statement of Account</h3>
            <p className="text-xs text-gray-500 mb-4">{client.clientName} ({client.clientCode})</p>
            <p className="text-xs text-gray-500 mb-4">Leave dates blank for an all-time statement.</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="text-gray-600 font-medium">From</span>
                <input type="date" value={soaFrom} onChange={e => setSoaFrom(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </label>
              <label className="text-xs">
                <span className="text-gray-600 font-medium">To</span>
                <input type="date" value={soaTo} onChange={e => setSoaTo(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
              </label>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <button type="button" onClick={() => {
                const y = new Date().getFullYear();
                const m = new Date().getMonth();
                const fyStart = m >= 3 ? `${y}-04-01` : `${y - 1}-04-01`;
                const fyEnd = m >= 3 ? `${y + 1}-03-31` : `${y}-03-31`;
                setSoaFrom(fyStart); setSoaTo(fyEnd);
              }} className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700">Current FY</button>
              <button type="button" onClick={() => { setSoaFrom(''); setSoaTo(''); }}
                className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700">Clear</button>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowSoa(false)} className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
              <button onClick={downloadSoa} disabled={soaBusy}
                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg">
                {soaBusy ? 'Generating…' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
              <th className="text-left px-4 py-3">Invoice #</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Task</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-right px-4 py-3">TDS</th>
              <th className="text-right px-4 py-3">Paid</th>
              <th className="text-right px-4 py-3">Outstanding</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-gray-400 py-8">No invoices</td></tr>
            ) : filtered.map(inv => {
              const total = Number(inv.totalAmount ?? inv.amount ?? 0);
              const tds = Number(inv.tdsAmount ?? 0);
              const paid = inv.payments.reduce((s, p) => s + Number(p.amount), 0);
              const out = outstanding(inv);
              const elsewhere = owedElsewhere(inv);
              return (
                <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">
                    {inv.invoiceNumber}
                    {elsewhere && <div className="font-sans text-[10px] text-amber-700">Billed to {inv.client?.clientName}</div>}
                    {!elsewhere && inv.serviceClient && <div className="font-sans text-[10px] text-indigo-600">For {inv.serviceClient.clientName}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{fmtDate(inv.invoiceDate)}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-[150px] truncate">{inv.task?.taskName || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${invStatusColor(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-800">
                    {fmt(total)}
                  </td>
                  {elsewhere ? (
                    <td colSpan={3} className="px-4 py-3 text-right text-xs text-gray-400">
                      Receivable from {inv.client?.clientName}
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {tds > 0 ? fmt(tds) : '—'}
                        {inv.tdsSection && <span className="text-xs text-gray-400 ml-1">({inv.tdsSection})</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-green-700">{paid > 0 ? fmt(paid) : '—'}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${out > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {out > 0 ? fmt(out) : 'Cleared'}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-gray-50 font-semibold text-sm">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-gray-600">Totals</td>
                <td className="px-4 py-3 text-right text-gray-800">
                  {fmt(filtered.reduce((s, i) => s + Number(i.totalAmount ?? i.amount ?? 0), 0))}
                </td>
                <td className="px-4 py-3 text-right text-gray-600">
                  {fmt(owedHere.reduce((s, i) => s + Number(i.tdsAmount ?? 0), 0))}
                </td>
                <td className="px-4 py-3 text-right text-green-700">
                  {fmt(owedHere.reduce((s, i) => s + i.payments.reduce((ps, p) => ps + Number(p.amount), 0), 0))}
                </td>
                <td className="px-4 py-3 text-right text-red-600">
                  {fmt(owedHere.reduce((s, i) => s + outstanding(i), 0))}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ── Compliance Tab ────────────────────────────────────────────────────────────

const ComplianceTab: React.FC<{ client: ClientData; tasks: Task[] }> = ({ client, tasks }) => {
  const udins = tasks.filter(t => t.udin).map(t => ({
    taskId: t.taskId, taskName: t.taskName, udin: t.udin!, status: t.status,
  }));

  return (
    <div className="space-y-6">
      {/* GSTINs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-800 text-sm">GSTIN / Addresses</h3>
        </div>
        {client.gstins.length === 0 ? (
          <p className="text-gray-400 text-sm p-5 text-center">No GSTIN entries</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {client.gstins.map(g => (
              <div key={g.id} className="px-5 py-4 flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-800">{g.label || '—'}</span>
                    {g.isPrimary && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Primary</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      g.gstType === 'REGISTERED' ? 'bg-green-100 text-green-700'
                      : g.gstType === 'EXPORT' ? 'bg-purple-100 text-purple-700'
                      : g.gstType === 'SEZ' ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600'}`}>
                      {g.gstType}
                    </span>
                  </div>
                  {g.gstin && <p className="font-mono text-sm text-gray-700 mt-1">{g.gstin}</p>}
                  {(g.city || g.state || g.pincode) && (
                    <p className="text-xs text-gray-500 mt-0.5">{[g.city, g.state, g.pincode].filter(Boolean).join(', ')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PAN */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-800 text-sm mb-3">PAN Details</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">PAN</span>
          <span className="font-mono text-base text-gray-800 bg-gray-50 px-3 py-1 rounded-lg border border-gray-200">
            {client.pan || '—'}
          </span>
        </div>
      </div>

      {/* TDS Certificates */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
          <h3 className="font-semibold text-gray-800 text-sm">TDS Certificates (26AS)</h3>
        </div>
        {client.tdsCertificates.length === 0 ? (
          <p className="text-gray-400 text-sm p-5 text-center">No TDS certificates</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Cert No.</th>
                  <th className="text-left px-4 py-3">Section</th>
                  <th className="text-left px-4 py-3">FY</th>
                  <th className="text-left px-4 py-3">Period</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {client.tdsCertificates.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{c.certificateNo}</td>
                    <td className="px-4 py-3 text-gray-600">{c.section}</td>
                    <td className="px-4 py-3 text-gray-600">{c.fiscalYear}</td>
                    <td className="px-4 py-3 text-gray-500">{c.period || '—'}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-800">{fmt(Number(c.amountAsPer26AS))}</td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(c.receivedDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* UDINs */}
      {udins.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-gray-800 text-sm">UDIN Records</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3">Task</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">UDIN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {udins.map(u => (
                  <tr key={u.taskId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{u.taskName}</p>
                      <p className="text-xs text-gray-400">{u.taskId}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor(u.status)}`}>
                        {u.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{u.udin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Documents Tab ─────────────────────────────────────────────────────────────

const DocumentsTab: React.FC<{ docs: VaultDoc[] }> = ({ docs }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
      <h3 className="font-semibold text-gray-800 text-sm">Document Vault ({docs.length})</h3>
    </div>
    {docs.length === 0 ? (
      <p className="text-gray-400 text-sm p-8 text-center">No documents in vault</p>
    ) : (
      <div className="divide-y divide-gray-50">
        {docs.map(d => (
          <div key={d.id} className="px-5 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-gray-800 text-sm truncate">{d.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {d.category && <span className="mr-2">{d.category.replace(/_/g, ' ')}</span>}
                {d.uploadedBy?.staffName} · {fmtDate(d.createdAt)}
              </p>
            </div>
            <a
              href={uploadsUrl(`client-vault/${d.fileName}`)}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
            >
              View ↗
            </a>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ── People Tab ────────────────────────────────────────────────────────────────

const PeopleTab: React.FC<{ people: ProfileData['people']; tasks: Task[] }> = ({ people, tasks }) => {
  const partnerIds = new Set(tasks.map(t => t.partner?.id).filter(Boolean));
  const managerIds = new Set(tasks.map(t => t.manager?.id).filter(Boolean));

  const sorted = [...people].sort((a, b) => {
    const rank = (p: typeof a) => p.role === 'Partner' ? 0 : p.role === 'Manager' ? 1 : 2;
    return rank(a) - rank(b);
  });

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-800 text-sm">People ({sorted.length})</h3>
      </div>
      {sorted.length === 0 ? (
        <p className="text-gray-400 text-sm p-8 text-center">No staff assigned to this client</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {sorted.map(p => (
            <div key={p.id} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-sm flex-shrink-0">
                  {p.staffName.charAt(0).toUpperCase()}
                </div>
                <p className="font-medium text-gray-800 text-sm">{p.staffName}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                p.role === 'Partner' ? 'bg-purple-100 text-purple-700'
                : p.role === 'Manager' ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600'
              }`}>{p.role}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Referrals Tab ─────────────────────────────────────────────────────────────

const ReferralsTab: React.FC<{ client: ClientData }> = ({ client }) => (
  <div className="space-y-6">
    {client.convertedFromLead && (
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h3 className="font-semibold text-blue-800 text-sm mb-1">Converted from Lead</h3>
        <p className="text-blue-700">{client.convertedFromLead.leadName}</p>
        <p className="text-xs text-blue-500 mt-1">{fmtDate(client.convertedFromLead.createdAt)}</p>
      </div>
    )}

    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
        <h3 className="font-semibold text-gray-800 text-sm">Referred Leads ({client.referredLeads.length})</h3>
      </div>
      {client.referredLeads.length === 0 ? (
        <p className="text-gray-400 text-sm p-8 text-center">No referrals from this client</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {client.referredLeads.map(l => (
            <div key={l.id} className="px-5 py-3 flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-gray-800 text-sm">{l.leadName}</p>
                <p className="text-xs text-gray-500">{fmtDate(l.createdAt)}</p>
              </div>
              {l.stage && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{l.stage}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const ClientProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isHR, isPartner } = useAuth();

  const [data, setData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const canAccess = isAdmin || isHR || isPartner;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getClientProfile(Number(id));
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load client profile');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/clients', { replace: true });
      return;
    }
    load();
  }, [canAccess, load, navigate]);

  if (!canAccess) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500 font-medium">{error || 'Client not found'}</p>
        <button onClick={() => navigate('/clients')} className="mt-4 text-blue-600 hover:underline text-sm">
          ← Back to Clients
        </button>
      </div>
    );
  }

  const { client, kpis } = data;

  return (
    <div className="space-y-3">
      {/* Compact Header Card — client info + KPIs side by side */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap lg:flex-nowrap">
          {/* Left: client identity */}
          <div className="min-w-0 flex-1">
            <button
              onClick={() => navigate('/clients')}
              className="text-xs text-gray-400 hover:text-blue-600 mb-1 inline-flex items-center gap-1"
            >← Clients</button>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 truncate">{client.clientName}</h1>
              <span className="text-[11px] font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded border border-gray-200">
                {client.clientCode}
              </span>
              {kpis.overdueTasks > 0 && (
                <span className="text-[11px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">
                  {kpis.overdueTasks} overdue
                </span>
              )}
            </div>
            {client.legalName && client.legalName !== client.clientName && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{client.legalName}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
              {client.pan && <span>PAN: <span className="font-mono text-gray-700">{client.pan}</span></span>}
              {client.phone && <span>📞 {client.phone}</span>}
              {client.address && <span className="max-w-xs truncate">📍 {client.address}</span>}
              {client.referringCaName && <span>🤝 Ref: {client.referringCaName}{client.referringCaPhone ? ` (${client.referringCaPhone})` : ''}</span>}
            </div>
          </div>

          {/* Right: compact KPI strip */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 lg:gap-3 w-full lg:w-auto lg:flex-shrink-0">
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-center min-w-[80px]">
              <p className={`text-base font-bold leading-tight ${kpis.openTasks > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                {kpis.openTasks}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Open</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-center min-w-[80px]">
              <p className={`text-base font-bold leading-tight ${kpis.overdueTasks > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {kpis.overdueTasks}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Overdue</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-center min-w-[80px]">
              <p className={`text-sm font-bold leading-tight ${kpis.totalOutstanding > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {fmt(kpis.totalOutstanding)}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Outstanding</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-center min-w-[80px]">
              <p className="text-sm font-bold leading-tight text-gray-800">{fmt(kpis.ytdBilling)}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">YTD</p>
            </div>
            <div className="bg-gray-50 rounded-lg px-3 py-2 text-center min-w-[80px]">
              <p className="text-sm font-bold leading-tight text-gray-800">{fmt(kpis.lifetimeBilling)}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">Lifetime</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + content in single card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <div className="flex min-w-max">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {activeTab === 'overview'    && <OverviewTab data={data} />}
          {activeTab === 'engagements' && <EngagementsTab tasks={data.tasks} />}
          {activeTab === 'financials'  && <FinancialsTab invoices={data.invoices} kpis={kpis} client={client} />}
          {activeTab === 'compliance'  && <ComplianceTab client={client} tasks={data.tasks} />}
          {activeTab === 'documents'   && <DocumentsTab docs={client.vaultDocuments} />}
          {activeTab === 'people'      && <PeopleTab people={data.people} tasks={data.tasks} />}
          {activeTab === 'referrals'   && <ReferralsTab client={client} />}
        </div>
      </div>
    </div>
  );
};

export default ClientProfile;
